/**
 * Root React Router layout and auth guard.
 *
 * On mount: calls /auth/refresh to restore session if refresh cookie is present.
 * While loading: shows a spinner.
 * If not authenticated: redirects to /login.
 * If authenticated: renders the AppShell with all protected routes.
 *
 * DEPENDS ON: authStore, React Router, api client
 */
import React, { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "./stores/authStore";
import { useDesignStore, applyDesign } from "./stores/designStore";
import { api } from "./lib/api";
import type { AuthResponse } from "@bosh-bordon/shared";

// Lazy-loaded pages for better initial load performance
const Login = React.lazy(() => import("./pages/Login"));
const AppShell = React.lazy(() => import("./components/layout/AppShell"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Prospects = React.lazy(() => import("./pages/Prospects"));
const Kanban = React.lazy(() => import("./pages/Kanban"));
const CallQueue = React.lazy(() => import("./pages/CallQueue"));
const ImportWizard = React.lazy(() => import("./pages/ImportWizard"));
const BulkEmailWorkspacePage = React.lazy(() => import("./pages/BulkEmailWorkspacePage"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Campaigns = React.lazy(() => import("./pages/Campaigns"));
const Analytics = React.lazy(() => import("./pages/Analytics"));
const AgendaPage = React.lazy(() => import("./pages/AgendaPage"));
const AutomationsPage = React.lazy(() => import("./pages/AutomationsPage"));

/** Requires authentication — redirects to /login if not authenticated. */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const { setAuth, clearAuth, setLoading, isAuthenticated } = useAuthStore();
  const { i18n } = useTranslation();
  const designPrefs = useDesignStore((s) => s.prefs);

  // Apply persisted design preferences on every mount
  useEffect(() => {
    applyDesign(designPrefs);
  }, []);

  // On app mount: attempt to restore session from refresh cookie
  useEffect(() => {
    if (isAuthenticated) {
      setLoading(false);
      return;
    }

    const restoreSession = async () => {
      try {
        // POST to refresh — sends the HttpOnly refresh cookie automatically
        const response = await fetch("/api/v1/auth/refresh", {
          method: "POST",
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json() as { success: boolean; data: { access_token: string } };
          if (data.success) {
            // Fetch user profile with the new access token
            const meResponse = await fetch("/api/v1/auth/me", {
              headers: { Authorization: `Bearer ${data.data.access_token}` },
            });

            if (meResponse.ok) {
              const meData = await meResponse.json() as { success: boolean; data: AuthResponse["user"] };
              if (meData.success) {
                setAuth(data.data.access_token, meData.data);
                // Apply user language preference
                return;
              }
            }
          }
        }

        // Refresh failed or no cookie present
        clearAuth();
      } catch {
        clearAuth();
      }
    };

    restoreSession();
  }, []);

  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      }
    >
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected routes — wrapped in AppShell */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="prospects" element={<Prospects />} />
          <Route path="prospects/import" element={<ImportWizard />} />
          <Route path="prospects/bulk-email" element={<BulkEmailWorkspacePage />} />
          <Route path="kanban" element={<Kanban />} />
          <Route path="calls" element={<CallQueue />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="settings" element={<Settings />} />
          <Route path="agenda" element={<AgendaPage />} />
          <Route path="automations" element={<AutomationsPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}
